import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiPost, endSession, getApiSettings } from "./Api/Client.js";
import { useIntranetData } from "./Api/useIntranetData.js";
import { LoginScreen } from "./Platform/LoginScreen.jsx";
import { ProfileScreen } from "./Platform/ProfileScreen.jsx";
import { RouteRenderer } from "./Platform/AppScreens.jsx";
import { routeFor } from "./Platform/routes.jsx";
import { AdminPanelScreen } from "./Platform/admin/AdminPanelScreen.jsx";
import { AppShell } from "./Platform/shell/AppShell.jsx";
import { LoadingScreen } from "./Platform/shell/LoadingScreen.jsx";
import { RouteLoading } from "./Platform/shell/RouteLoading.jsx";
import { useOperatorCompany } from "./Platform/shell/useOperatorCompany.js";
import { useSessionGuards } from "./Platform/shell/useSessionGuards.js";
import { CultureCheckScreen } from "./Products/hiring/CultureCheckScreen.jsx";
import { JoiningScreen } from "./Products/hiring/JoiningScreen.jsx";
import { useSuggestedTasksMenu, withSuggestedTasksAccess } from "./Products/axon/index.js";
import { resolveActiveEmployee } from "./Shared/ScreenUtils.jsx";
import { hasPageAccess, runsCompany } from "./Services/pageAccess.js";

// An old address that moved: go to the new one once, after render. It replaces the old entry rather than adding one,
// otherwise Back would land on the old address, which redirects forward again, and the person could never go back past it.
function MovedTo({ navigate, to }) {
  useEffect(() => { navigate(to, { replace: true }); }, [navigate, to]);
  return null;
}

const here = () => window.location.pathname + window.location.search;

function App() {
  const [settings, setSettings] = useState(getApiSettings);
  const [route, setRoute] = useState(here);
  const [reloadKey, setReloadKey] = useState(0);
  const path = route.split("?")[0];

  const isLoginRoute = path.startsWith("/login");
  // Offer emails sent before 9 Sep 2026 link to /offer/accept/<token>. The old page is retired; the joining flow is the
  // only way to accept.
  const isOldOfferRoute = path.startsWith("/offer/accept/");

  // "Signed in" is the server's session cookie; this mark only says the browser signed in, and a 401 clears it.
  const hasAuth = Boolean(settings.signedIn);
  const { data: loaded, loaded: answered, loading, errors, reload } = useIntranetData(reloadKey, hasAuth && !isLoginRoute && !isOldOfferRoute);
  // Suggested Tasks opens for anyone with a suggestion to decide, not only for page holders: the server answers, and
  // the menu and the route follow it (Products/axon).
  const showSuggestedTasks = useSuggestedTasksMenu(hasAuth && !isLoginRoute && !isOldOfferRoute && Boolean(loaded?.me));
  const data = useMemo(() => withSuggestedTasksAccess(loaded, showSuggestedTasks), [loaded, showSuggestedTasks]);
  // A page that needs lists the frame does not load for everyone (Platform/routes.jsx) waits for them.
  const waiting = (routeFor(path, data?.me).needs || []).filter((key) => !answered[key]);
  const ready = !loading && hasPageAccess(data?.me);
  useEffect(() => { if (ready && waiting.length) reload(waiting); }, [ready, waiting.join(",")]);
  const selectedEmployeeId = useMemo(() => String(resolveActiveEmployee(data)?.id ?? ""), [data.employees, data.me]);

  useEffect(() => {
    const onPop = () => setRoute(here());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to, opts) => {
    if (opts?.replace) window.history.replaceState(null, "", to);
    else window.history.pushState(null, "", to);
    setRoute(here());
  }, []);

  useOperatorCompany(data?.me, reload);
  useSessionGuards({ hasAuth, isLoginRoute, errors });

  const login = (to = "/home/") => { setSettings(getApiSettings()); setReloadKey((v) => v + 1); navigate(to); };
  const retryAppLoad = useCallback(() => setReloadKey((v) => v + 1), []);
  const logout = async () => {
    try { await apiPost("/Users/Auth/Logout/"); } catch { /* the session ends here either way */ }
    endSession();
  };

  // Redirects happen in an effect, after every hook has run, never while rendering. A signed-in person who types
  // /login/ is sent home instead of getting the sign-in form while the session is valid.
  const redirecting = isOldOfferRoute || (hasAuth && isLoginRoute);
  useEffect(() => {
    if (isOldOfferRoute) window.location.replace("/join/" + path.replace("/offer/accept/", ""));
    else if (hasAuth && isLoginRoute) navigate("/home/");
  }, [isOldOfferRoute, hasAuth, isLoginRoute, path, navigate]);
  if (redirecting) return null;

  // The only pages a person with no account can open: anyone who has the URL reaches them, so keep this list short.
  // A candidate has no account yet — that is the point of these pages — so sending them to the sign-in screen defeats
  // them. Checked after every hook has run, so the hook order never changes between renders.
  if (path.startsWith("/join/")) return <JoiningScreen />;
  if (path.startsWith("/onboarding/culture")) return <CultureCheckScreen />;

  if (!hasAuth) return <LoginScreen settings={settings} onLogin={login} />;

  // Wait for the page-access grid before drawing the shell, so nobody sees a menu with entries missing while it is in flight.
  if (!ready) return <LoadingScreen onRetry={retryAppLoad} />;

  // Old addresses: the admin panel moved to /company-admin/, and Plan and Plugins became its "Modules and add-ons".
  if (path.startsWith("/admin-panel")) return <MovedTo navigate={navigate} to="/company-admin/" />;
  if (path.startsWith("/plan-and-plugins")) return <MovedTo navigate={navigate} to="/company-admin/?section=modules" />;
  // The company admin panel is its own full-screen layout (own menu and Exit). It lives at /company-admin/, not /admin/,
  // because nginx sends /admin/ to Django's own admin. The old /admin/change-password/ address is still the Change
  // Password screen, so it is left to the router below.
  if ((path === "/company-admin" || path.startsWith("/company-admin/")) && runsCompany(data.me)) {
    return waiting.length ? <RouteLoading /> : <AdminPanelScreen data={data} navigate={navigate} logout={logout} reload={reload} />;
  }

  return (
    <AppShell route={route} navigate={navigate} data={data} loading={loading} errors={errors} reload={reload}>
      {waiting.length ? <RouteLoading /> : path.startsWith("/profile")
        ? <ProfileScreen data={data} onLogout={logout} reload={reload} navigate={navigate} />
        : <RouteRenderer route={route} data={data} settings={settings} selectedEmployeeId={selectedEmployeeId} reload={reload} navigate={navigate} loading={loading} />}
    </AppShell>
  );
}

export default App;
